// getVipRecommendUserIds.js
'use strict';
import { createSelector } from '../resource/createCachedSelector.js';

import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getUserDataList from '../selector/getUserDataList.js';
import { VIP_RECOMMEND_USER_IDS } from '../RemoteConfigKeys.js';
import { EMPTY_ARRAY } from '../resource/defaults.js';

const VIP_RECOMMEND_USER_MAX_COUNT = 4;

/**
 * Select the VIP recommend user ids from remote config, empty entries dropped.
 * Not capped here so a failed id can be backfilled — the display cap lives in
 * getVipRecommendUsers.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @returns {string[]} configured recommend user ids.
 * @example
 * getVipRecommendUserIds(state);
 * // => ['userId1', 'userId2']
 */
const getVipRecommendUserIds = createSelector(
  state => getRemoteConfigData(state, VIP_RECOMMEND_USER_IDS),
  (recommendUserIds = EMPTY_ARRAY) => recommendUserIds.filter(Boolean)
);

/**
 * Select the loaded, displayable recommend users (has username), capped at
 * VIP_RECOMMEND_USER_MAX_COUNT. Capping after the load check lets a failed id
 * be backfilled by the next configured one.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @returns {object[]} displayable recommend users.
 * @example
 * getVipRecommendUsers(state);
 * // => [{ id: 'userId1', username: 'username1' }]
 */
export const getVipRecommendUsers = createSelector(
  state => getUserDataList(state, getVipRecommendUserIds(state)),
  (recommendUsers = EMPTY_ARRAY) =>
    recommendUsers
      .filter(user => user.username)
      .slice(0, VIP_RECOMMEND_USER_MAX_COUNT)
);

export default getVipRecommendUserIds;
